/**
 * Organization Category Service
 * Manages custom category templates for organizations
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { requireAuthenticatedContext } from '@/lib/auth';
import { logger } from '@/lib/logger';

export type DomainType = 'lighting_fixtures' | 'lighting_controls' | 'lv_products';
export type IconType = 'abbreviation' | 'svg';
export type IconShape = 'square' | 'circle';

export interface OrganizationCategory {
  id: string;
  organization_id: string;
  domain: DomainType;
  category: string;
  subcategory: string | null;
  parent_category_id: string | null;
  display_name: string;
  description: string | null;
  display_order: number;
  icon_type: IconType;
  abbreviation: string;
  icon_shape: IconShape;
  icon_width: number;
  icon_height: number;
  fill_color: string;
  border_color: string;
  text_color: string;
  icon_svg: string | null;
  icon_svg_original_width: number | null;
  icon_svg_original_height: number | null;
  default_scale: number;
  is_active: boolean;
  created_by: string;
  created_by_clerk_id: string | null; // Clerk user ID for cross-org visibility
  created_at: string;
  updated_at: string;
}

export interface CreateCategoryDto {
  domain?: DomainType;
  category: string;
  subcategory?: string | null;
  parent_category_id?: string | null;
  display_name?: string;
  description?: string | null;
  display_order?: number;
  icon_type?: IconType;
  abbreviation?: string;
  icon_shape?: IconShape;
  icon_width?: number;
  icon_height?: number;
  fill_color?: string;
  border_color?: string;
  text_color?: string;
  icon_svg?: string | null;
  icon_svg_original_width?: number | null;
  icon_svg_original_height?: number | null;
  default_scale?: number;
}

export interface UpdateCategoryDto extends Partial<CreateCategoryDto> {
  is_active?: boolean;
}

class OrganizationCategoryService {
  private async getAuthContext() {
    const context = await requireAuthenticatedContext();
    if (!context?.userId || !context?.organizationId) {
      throw new Error('Authentication and organization context required');
    }

    const supabaseClient = await getAuthenticatedSupabaseClient();
    return {
      supabaseClient,
      currentOrgId: context.organizationId,
      userId: context.userId,
    };
  }

  /**
   * Get all active categories accessible to the current user
   * RLS handles filtering: returns categories created by user OR in their current org
   */
  async getCategories(domain?: DomainType): Promise<OrganizationCategory[]> {
    const { supabaseClient } = await this.getAuthContext();

    // RLS policy "View own or org categories" handles filtering:
    // - Categories created by this user (visible across all their orgs)
    // - Categories in the current organization (created by anyone)
    let query = supabaseClient
      .from('organization_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .order('category', { ascending: true });

    if (domain) {
      query = query.eq('domain', domain);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Failed to fetch categories', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get a single category by ID
   */
  async getCategory(id: string): Promise<OrganizationCategory | null> {
    const { supabaseClient } = await this.getAuthContext();

    const { data, error } = await supabaseClient
      .from('organization_categories')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      logger.error('Failed to fetch category', error);
      throw error;
    }

    return data;
  }

  /**
   * Create a new category
   * If a soft-deleted category exists with the same path, reactivate it instead
   */
  async createCategory(dto: CreateCategoryDto): Promise<OrganizationCategory> {
    const { supabaseClient, currentOrgId, userId } = await this.getAuthContext();

    // Check for soft-deleted record with same path and reactivate it
    const domain = dto.domain || 'lv_products';
    const existingInactive = await this.findInactiveByPath(
      domain,
      dto.category,
      dto.subcategory || null,
    );

    if (existingInactive) {
      // Reactivate and update the existing record
      logger.log('Reactivating soft-deleted category:', existingInactive.id);
      return this.updateCategory(existingInactive.id, {
        ...dto,
        is_active: true,
      });
    }

    // Create new record
    const categoryData = {
      organization_id: currentOrgId,
      domain,
      category: dto.category,
      subcategory: dto.subcategory || null,
      parent_category_id: dto.parent_category_id || null,
      display_name: dto.display_name || dto.category,
      description: dto.description || null,
      display_order: dto.display_order ?? 0,
      icon_type: dto.icon_type || 'abbreviation',
      abbreviation: dto.abbreviation || null, // Will be auto-generated by trigger if null
      icon_shape: dto.icon_shape || 'square',
      icon_width: dto.icon_width ?? 24,
      icon_height: dto.icon_height ?? 24,
      fill_color: dto.fill_color || '#6B7280',
      border_color: dto.border_color || '#374151',
      text_color: dto.text_color || '#FFFFFF',
      icon_svg: dto.icon_svg || null,
      icon_svg_original_width: dto.icon_svg_original_width || null,
      icon_svg_original_height: dto.icon_svg_original_height || null,
      default_scale: dto.default_scale ?? 1.0,
      created_by: userId,
      created_by_clerk_id: userId, // Enable cross-org visibility for the creating user
    };

    const { data, error } = await supabaseClient
      .from('organization_categories')
      .insert(categoryData)
      .select()
      .single();

    if (error) {
      logger.error('Failed to create category', error);
      throw error;
    }

    return data;
  }

  /**
   * Update an existing category
   */
  async updateCategory(id: string, updates: UpdateCategoryDto): Promise<OrganizationCategory> {
    const { supabaseClient } = await this.getAuthContext();

    // Remove undefined values
    const cleanUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, v]) => v !== undefined),
    );

    const { data, error } = await supabaseClient
      .from('organization_categories')
      .update(cleanUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      logger.error('Failed to update category', error);
      throw error;
    }

    return data;
  }

  /**
   * Reset icon to default (clears icon fields but keeps category record)
   * Use this when user clicks "Reset to default" in the Icon section
   */
  async resetIconToDefault(id: string): Promise<OrganizationCategory> {
    const { supabaseClient } = await this.getAuthContext();

    const { data, error } = await supabaseClient
      .from('organization_categories')
      .update({
        icon_type: 'abbreviation', // Reset to abbreviation type
        icon_svg: null, // Clear custom SVG
        abbreviation: null, // Let trigger auto-generate
        fill_color: '#6B7280', // Reset to default gray
        border_color: '#374151',
        text_color: '#FFFFFF',
        icon_shape: 'square',
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      logger.error('Failed to reset icon to default', error);
      throw error;
    }

    return data;
  }

  /**
   * Soft delete a category (mark as inactive)
   */
  async deleteCategory(id: string): Promise<void> {
    const { supabaseClient } = await this.getAuthContext();

    const { error } = await supabaseClient
      .from('organization_categories')
      .update({ is_active: false })
      .eq('id', id);

    if (error) {
      logger.error('Failed to delete category', error);
      throw error;
    }
  }

  /**
   * Hard delete a category (permanent)
   */
  async permanentlyDeleteCategory(id: string): Promise<void> {
    const { supabaseClient } = await this.getAuthContext();

    const { error } = await supabaseClient.from('organization_categories').delete().eq('id', id);

    if (error) {
      logger.error('Failed to permanently delete category', error);
      throw error;
    }
  }

  /**
   * Get categories organized by parent-child hierarchy
   */
  async getCategoriesHierarchy(domain?: DomainType): Promise<{
    parents: OrganizationCategory[];
    children: Map<string | null, OrganizationCategory[]>;
  }> {
    const categories = await this.getCategories(domain);

    const parents = categories.filter(c => !c.parent_category_id && !c.subcategory);
    const children = new Map<string | null, OrganizationCategory[]>();

    // Group children by parent
    for (const cat of categories) {
      if (cat.parent_category_id || cat.subcategory) {
        const parentKey = cat.parent_category_id || cat.category;
        if (!children.has(parentKey)) {
          children.set(parentKey, []);
        }
        children.get(parentKey)!.push(cat);
      }
    }

    return { parents, children };
  }

  /**
   * Reorder categories
   */
  async reorderCategories(orderedIds: string[]): Promise<void> {
    const { supabaseClient } = await this.getAuthContext();

    // Update display_order for each category
    const updates = orderedIds.map((id, index) =>
      supabaseClient.from('organization_categories').update({ display_order: index }).eq('id', id),
    );

    const results = await Promise.all(updates);

    const errors = results.filter(r => r.error);
    if (errors.length > 0) {
      logger.error('Failed to reorder some categories', errors);
      throw new Error('Failed to reorder categories');
    }
  }

  /**
   * Check if a category name already exists for the organization
   */
  async categoryExists(
    category: string,
    domain: DomainType,
    subcategory?: string | null,
    excludeId?: string,
  ): Promise<boolean> {
    const { supabaseClient, currentOrgId } = await this.getAuthContext();

    let query = supabaseClient
      .from('organization_categories')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', currentOrgId)
      .eq('domain', domain)
      .eq('category', category)
      .eq('is_active', true);

    if (subcategory) {
      query = query.eq('subcategory', subcategory);
    } else {
      query = query.is('subcategory', null);
    }

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { count, error } = await query;

    if (error) {
      logger.error('Failed to check category existence', error);
      throw error;
    }

    return (count || 0) > 0;
  }

  /**
   * Get a category by path (domain, category, subcategory)
   * Similar to categoryTemplateService.getByPath but for organization-specific categories
   */
  async getByPath(
    domain: DomainType,
    category: string,
    subcategory?: string | null,
  ): Promise<OrganizationCategory | null> {
    try {
      const { supabaseClient, currentOrgId } = await this.getAuthContext();

      let query = supabaseClient
        .from('organization_categories')
        .select('*')
        .eq('organization_id', currentOrgId)
        .eq('domain', domain)
        .eq('category', category)
        .eq('is_active', true);

      if (subcategory) {
        query = query.eq('subcategory', subcategory);
      } else {
        query = query.is('subcategory', null);
      }

      const { data, error } = await query.maybeSingle();

      if (error) {
        logger.error('Failed to fetch category by path', error);
        return null;
      }

      return data;
    } catch (error) {
      // Authentication may not be available (e.g., in public view)
      logger.debug('getByPath failed (possibly unauthenticated):', error);
      return null;
    }
  }

  /**
   * Find an inactive (soft-deleted) category by path
   * Used to reactivate soft-deleted categories instead of creating duplicates
   */
  async findInactiveByPath(
    domain: DomainType,
    category: string,
    subcategory?: string | null,
  ): Promise<OrganizationCategory | null> {
    try {
      const { supabaseClient, currentOrgId } = await this.getAuthContext();

      let query = supabaseClient
        .from('organization_categories')
        .select('*')
        .eq('organization_id', currentOrgId)
        .eq('domain', domain)
        .eq('category', category)
        .eq('is_active', false); // Looking for inactive records

      if (subcategory) {
        query = query.eq('subcategory', subcategory);
      } else {
        query = query.is('subcategory', null);
      }

      const { data, error } = await query.maybeSingle();

      if (error) {
        logger.error('Failed to find inactive category', error);
        return null;
      }

      return data;
    } catch (error) {
      logger.debug('findInactiveByPath failed:', error);
      return null;
    }
  }
}

export const organizationCategoryService = new OrganizationCategoryService();
